import { createClient, type SupabaseClient, AuthApiError } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Supabase URL or Anon Key is missing. Please check your .env file.');
}

const globalForSupabase = globalThis as typeof globalThis & { __adliticsSupabase?: SupabaseClient };

const EXPIRED_ACCESS_PURGE_MS = 14 * 24 * 60 * 60 * 1000;
const ACCESS_TOKEN_REFRESH_SKEW_MS = 60_000;

export const AUTH_SESSION_INVALID_EVENT = 'auth:session-invalid';
export const UNAUTHENTICATED_MSG = 'Usuário não autenticado. Faça login novamente.';

const INVALID_AUTH_CODES = new Set([
    'refresh_token_not_found', 'invalid_grant', 'bad_jwt', 'session_not_found',
]);
const INVALID_AUTH_MSG_RE =
    /invalid refresh token|refresh token not found|invalid claim: missing sub|auth session missing|session not found/i;

const AUTH_CODE_MESSAGES: Record<string, string> = {
    invalid_credentials: 'E-mail ou senha inválidos.',
    invalid_grant: 'E-mail ou senha inválidos.',
    email_not_confirmed: 'Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.',
    user_banned: 'Esta conta foi suspensa. Entre em contato com o suporte.',
    over_request_rate_limit: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
    over_email_send_rate_limit: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
    validation_failed: 'E-mail ou senha em formato inválido.',
};

type StoredAuthSession = { refresh_token?: string; expires_at?: number; access_token?: string };
type AuthErrorLike = { name?: string; code?: string; status?: number; message?: string };

function errorToMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error ?? '');
}

export function isUnauthenticatedClientError(error: unknown): boolean {
    const msg = errorToMessage(error);
    return msg === UNAUTHENTICATED_MSG || /usuário não autenticado|faça login novamente/i.test(msg);
}

function getAuthStorageKey(url: string): string | null {
    try {
        return `sb-${new URL(url).hostname.split('.')[0]}-auth-token`;
    } catch {
        return null;
    }
}

function removeAuthStorageKeys(storageKey: string): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(storageKey);
    localStorage.removeItem(`${storageKey}-user`);
    localStorage.removeItem(`${storageKey}-code-verifier`);
}

function wipePersistedAuthStorage(): void {
    if (!supabaseUrl) return;
    const key = getAuthStorageKey(supabaseUrl);
    if (key) removeAuthStorageKeys(key);
}

function decodeJwtPayload(token?: string): Record<string, unknown> | null {
    if (!token) return null;
    try {
        const [, payload] = token.split('.');
        if (!payload) return null;
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
        return decoded && typeof decoded === 'object' ? decoded : null;
    } catch {
        return null;
    }
}

/** `exp` JWT em ms epoch, ou null. */
function jwtExpMs(token?: string): number | null {
    const exp = decodeJwtPayload(token)?.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
}

/** Anon/service JWTs (sem `sub`) → GET /auth/v1/user 403 bad_jwt. */
function isNonUserAccessToken(token?: string): boolean {
    if (!token) return false;
    const claims = decodeJwtPayload(token);
    if (!claims) return true;
    const role = typeof claims.role === 'string' ? claims.role : '';
    if (role === 'anon' || role === 'service_role') return true;
    return typeof claims.sub !== 'string' || claims.sub.length === 0;
}

function isAccessTokenExpired(token?: string): boolean {
    const expMs = jwtExpMs(token);
    return expMs !== null && expMs <= Date.now();
}

function purgeCorruptAuthStorage(url: string): void {
    if (typeof window === 'undefined') return;
    const storageKey = getAuthStorageKey(url);
    if (!storageKey) return;

    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key?.startsWith('sb-') || !key.endsWith('-auth-token') || key === storageKey) continue;
        removeAuthStorageKeys(key);
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    try {
        const parsed = JSON.parse(raw) as StoredAuthSession & {
            currentSession?: StoredAuthSession;
            expiresAt?: number;
        };
        const session = parsed.currentSession ?? parsed;
        const expSec = session.expires_at ?? parsed.expiresAt ?? decodeJwtPayload(session.access_token)?.exp;

        if (!session?.refresh_token || isNonUserAccessToken(session.access_token)) {
            removeAuthStorageKeys(storageKey);
            return;
        }
        if (typeof expSec === 'number' && Date.now() - expSec * 1000 > EXPIRED_ACCESS_PURGE_MS) {
            removeAuthStorageKeys(storageKey);
        }
    } catch {
        removeAuthStorageKeys(storageKey);
    }
}

export function isInvalidAuthSessionError(error: unknown): boolean {
    const msg = errorToMessage(error);
    if (error && typeof error === 'object') {
        const e = error as AuthErrorLike;
        const code = String(e.code ?? '').toLowerCase();
        const errMsg = String(e.message ?? '');
        if (e.name === 'AuthSessionMissingError' || INVALID_AUTH_CODES.has(code)) return true;
        if (e.status === 400 && /refresh/i.test(errMsg)) return true;
        if (e.status === 403 && /sub claim|bad.?jwt|session not found|session missing/i.test(errMsg)) return true;
    }
    return INVALID_AUTH_MSG_RE.test(msg);
}

/** Silencia console.error esperado do GoTrue em refresh revogado. */
function installInvalidAuthConsoleFilter(): void {
    const err = console.error as typeof console.error & { __adliticsAuthFilter?: boolean };
    if (typeof console === 'undefined' || err.__adliticsAuthFilter) return;
    const original = console.error.bind(console);
    const filtered = (...args: unknown[]) => {
        if (args.some((arg) => isInvalidAuthSessionError(arg))) return;
        original(...args);
    };
    (filtered as { __adliticsAuthFilter?: boolean }).__adliticsAuthFilter = true;
    console.error = filtered as typeof console.error;
}

function createSupabaseClient(): SupabaseClient {
    if (supabaseUrl) purgeCorruptAuthStorage(supabaseUrl);
    return createClient(supabaseUrl || '', supabaseAnonKey || '', {
        db: { schema: 'public' },
        auth: { flowType: 'pkce', autoRefreshToken: true, persistSession: true, detectSessionInUrl: true },
    });
}

installInvalidAuthConsoleFilter();
export const supabase = globalForSupabase.__adliticsSupabase ?? createSupabaseClient();
if (import.meta.env.DEV) globalForSupabase.__adliticsSupabase = supabase;

export function getSupabaseConfigError(): string | null {
    if (!supabaseUrl || !supabaseAnonKey) return 'Configuração do Supabase ausente. Verifique o arquivo .env.';
    if (!supabaseAnonKey.startsWith('eyJ') || supabaseAnonKey.includes('sua-chave')) {
        return 'Chave anon inválida. Copie em Supabase → Project Settings → API → anon public.';
    }
    return null;
}

export async function probeSupabaseAnonKey(): Promise<string | null> {
    const configError = getSupabaseConfigError();
    if (configError) return configError;
    try {
        const res = await fetch(`${supabaseUrl}/auth/v1/health`, {
            headers: { apikey: supabaseAnonKey! },
            signal: AbortSignal.timeout(12000),
        });
        if (res.ok) return null;
        if (res.status >= 500) {
            return 'Supabase sobrecarregado ou em manutenção. Aguarde alguns minutos e tente de novo.';
        }
        return null;
    } catch (err) {
        if (err instanceof DOMException && err.name === 'TimeoutError') {
            return 'Supabase não respondeu a tempo (projeto pode estar Unhealthy). Tente login mesmo assim ou aguarde 5 min.';
        }
        return 'Não foi possível alcançar o Supabase. Verifique rede ou status do projeto no dashboard.';
    }
}

/** Limpa memória sem POST /logout (JWT morto → 403 no Network). */
async function removeSessionWithoutRemoteLogout(): Promise<void> {
    const auth = supabase.auth as unknown as { _removeSession?: () => Promise<void> };
    if (typeof auth._removeSession === 'function') {
        await auth._removeSession();
        return;
    }
    try {
        await supabase.auth.signOut({ scope: 'local' });
    } catch {
        /* ignore */
    }
}

let clearLocalAuthSessionPromise: Promise<void> | null = null;

export async function clearLocalAuthSession(): Promise<void> {
    if (!clearLocalAuthSessionPromise) {
        clearLocalAuthSessionPromise = (async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                const token = session?.access_token;
                const skipRemote =
                    !token || isNonUserAccessToken(token) || isAccessTokenExpired(token);
                if (skipRemote) {
                    await removeSessionWithoutRemoteLogout();
                } else {
                    await supabase.auth.signOut({ scope: 'local' });
                }
            } catch {
                try {
                    await removeSessionWithoutRemoteLogout();
                } catch {
                    /* ignore */
                }
            }
            wipePersistedAuthStorage();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent(AUTH_SESSION_INVALID_EVENT));
            }
        })().finally(() => {
            clearLocalAuthSessionPromise = null;
        });
    }
    return clearLocalAuthSessionPromise;
}

async function clearIfInvalidAuth(error: unknown): Promise<void> {
    if (isInvalidAuthSessionError(error)) await clearLocalAuthSession();
}

async function clearSessionAndReject(): Promise<never> {
    await clearLocalAuthSession();
    throw new Error(UNAUTHENTICATED_MSG);
}

/** Access token de usuário válido; evita GET /auth/v1/user (403 bad_jwt com anon/JWT inválido). */
export async function getValidAccessToken(): Promise<string> {
    let { data: { session } } = await supabase.auth.getSession();
    let token = session?.access_token;

    if (token && isNonUserAccessToken(token)) {
        return clearSessionAndReject();
    }

    // Sem claim `exp` → não força refresh (comportamento anterior).
    const expMs = jwtExpMs(token);
    const needsRefresh =
        !token || (expMs !== null && expMs < Date.now() + ACCESS_TOKEN_REFRESH_SKEW_MS);

    if (needsRefresh) {
        const refreshed = await supabase.auth.refreshSession();
        if (refreshed.error) {
            await clearIfInvalidAuth(refreshed.error);
            throw new Error(UNAUTHENTICATED_MSG);
        }
        session = refreshed.data.session;
        token = session?.access_token;
    }

    if (!token || isNonUserAccessToken(token)) {
        return clearSessionAndReject();
    }
    return token;
}

export function mapSupabaseAuthError(error: unknown): string {
    if (error instanceof AuthApiError) {
        const mapped = error.code ? AUTH_CODE_MESSAGES[error.code] : undefined;
        if (mapped) return mapped;
        if (error.status === 400 && /invalid login/i.test(error.message)) {
            return AUTH_CODE_MESSAGES.invalid_credentials;
        }
    }
    const message = errorToMessage(error);
    if (/failed to fetch|network/i.test(message)) {
        return 'Autenticação do Supabase indisponível. Verifique sua conexão ou o status do projeto.';
    }
    return message || 'Falha ao autenticar. Tente novamente.';
}

let authRecoveryPromise: Promise<void> | null = null;

/** Valida sessão persistida no boot; descarta tokens inválidos/revogados. */
export async function recoverStaleAuthSession(): Promise<void> {
    if (authRecoveryPromise) return authRecoveryPromise;

    authRecoveryPromise = (async () => {
        try {
            await supabase.auth.initialize();
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.refresh_token) return;
            if (isNonUserAccessToken(session.access_token)) {
                await clearLocalAuthSession();
                return;
            }
            // Sem `exp` → tenta refresh (≠ getValidAccessToken). Evita getUser() 403.
            const expMs = jwtExpMs(session.access_token);
            if (expMs !== null && expMs > Date.now() + ACCESS_TOKEN_REFRESH_SKEW_MS) return;
            const { error } = await supabase.auth.refreshSession();
            if (error) await clearIfInvalidAuth(error);
        } catch (error) {
            await clearIfInvalidAuth(error);
        }
    })();

    return authRecoveryPromise;
}
